import {
  changeTransactionSignaturePicturePriorityForAll,
  MoveSignature,
  moveTransactionSignature,
  PicturePriority,
} from "@/state/transactions/CurrentTransactionEvents";
import { Picture } from "@/type/core/offices/offices.types";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import { SignaturePicturePriorityEnum } from "@/utils/enums/signature.picture.priority.enum";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import EditIcon from "@static/core/icons/icon-edit.svg?react";

import { Signature } from "@type/core/dashboard/dashboard.types";

import useSignatoryCard from "@/utils/hooks/useSignatoryCard";
import { Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import ChooseSignatureImageModal from "@components/core/modals/ChooseSignatureImageModal";
import SignatoryCardSignedByText from "@components/core/subComponents/SignatoryCard/SignatoryCardSignedByText";
import SignatoryCardShadow from "@components/core/subComponents/SignatoryCard/SignatoryCardShadow";
import SignatureHelper from "@utils/helpers/signature-helper";
import SignatureCounter from "@components/core/subComponents/SignatureCounter/SignatureCounter";
import useSignatoryCardPicture from "@components/core/subComponents/SignatoryCard/useSignatoryCardPicture";

interface SignatoryCardProps {
  documentRank: number;
  transactionType: TransactionType;
  email: string;
  firstname: string;
  lastname: string;
  signatorySignaturePictures?: Picture[];
  signatures: Signature[];
  setManualPositioning: Dispatch<SetStateAction<boolean>>;
  className?: string;
  isUserInOffice: boolean;
  pageNumber: number;
  signaturePictureRank?: number;
  signaturePriorityRule?: SignaturePicturePriorityEnum;
  officeId: string;
  signatureIdentificator: string;
  isDisabled?: boolean;
  isSignatoryCounterDisabled?: boolean;
  signatureCount?: number;
  cardDimensionHeight: number;
  cardDimensionWidth: number;
}

const SignatoryCard: FunctionComponent<SignatoryCardProps> = ({
  documentRank,
  transactionType,
  email,
  firstname,
  lastname,
  signatorySignaturePictures,
  signatures,
  setManualPositioning,
  className = "",
  isUserInOffice,
  pageNumber,
  signaturePictureRank,
  signaturePriorityRule,
  officeId,
  signatureIdentificator,
  isDisabled = false,
  isSignatoryCounterDisabled = false,
  signatureCount,
  cardDimensionHeight,
  cardDimensionWidth,
}) => {
  const { t } = useTranslation();

  const onSetSignature = (movedSignature: MoveSignature) => {
    const order =
      movedSignature.order === undefined || movedSignature.order === -1
        ? signatures.length
        : movedSignature.order + 1;
    const newSignature: Signature = {
      ...movedSignature,
      id: undefined,
      order,
      pattern: signature?.pattern ?? movedSignature.pattern,
      signaturePictureRank: signature?.signaturePictureRank,
      signaturePriorityRule: signature?.signaturePriorityRule,
      identificator: signature?.identificator,
    };
    moveTransactionSignature(newSignature);
  };

  const [chooseImageModalVisible, setChooseImageModalVisible] =
    useState<boolean>(false);

  const {
    signatorySignaturePicturesWithCorrectUrl,
    signature,
    officeSignaturePictures,
    src,
  } = useSignatoryCardPicture(
    officeId,
    isUserInOffice,
    signatures,
    signatorySignaturePictures,
    [],
    documentRank,
    email,
    transactionType,
    "",
    signatureIdentificator,
  );

  const { DraggableCard, signatureImageLeftInitialPosition } = useSignatoryCard(
    {
      rank: documentRank,
      transactionType,
      signature,
      signatures,
      setManualPositioning,
      className,
      pageNumber,
      onSetSignature,
      signatureIdentificator,
      isDisabled,
    },
  );

  const handleSubmitChooseSignature = (
    signature: Pick<
      Signature,
      "signaturePictureRank" | "signaturePriorityRule"
    >,
  ) => {
    const { signaturePictureRank, signaturePriorityRule } = signature;
    const picturePriority: PicturePriority = {
      email,
      signaturePictureRank,
      signaturePriorityRule,
    };
    changeTransactionSignaturePicturePriorityForAll(picturePriority);
  };

  let isFirstSignature = false;
  if (signatureIdentificator) {
    isFirstSignature =
      SignatureHelper.getSignatureIndexFromIdentificator(
        signatureIdentificator,
      ) === 0;
  }
  const signatoryName = `${firstname} ${lastname}`;
  return (
    <div
      className={`d-flex ${
        !signature?.isMoved ? "position-relative" : "position-absolute"
      } mt-8`}
      style={{ height: cardDimensionHeight }}
    >
      <div className="d-flex align-items-center font-extra-small w-200px">
        {isFirstSignature && <>{signatoryName}</>}
      </div>
      <>
        {isFirstSignature && signatureIdentificator !== undefined && (
          <SignatoryCardShadow
            documentRank={documentRank}
            transactionType={transactionType}
            email={email}
            firstname={firstname}
            lastname={lastname}
            signatorySignaturePictures={signatorySignaturePictures}
            signatures={signatures}
            officeId={officeId}
            isUserInOffice={isUserInOffice}
            left={`${signatureImageLeftInitialPosition}px`}
            signatureIdentificator={signatureIdentificator}
          />
        )}
        <DraggableCard data-test={"card"} data-alt={signatoryName}>
          {signature && (
            <SignatoryCardSignedByText
              firstname={firstname}
              lastname={lastname}
              cardDimensionWidth={cardDimensionWidth}
              transactionType={transactionType}
            />
          )}
          <img
            src={src}
            className="signatory-image"
            data-test="card"
            data-alt={signatoryName}
            alt=""
          />
        </DraggableCard>
        {signature &&
          signature.pattern &&
          signature.pattern !== SignaturePatternEnum.DEFAULT &&
          isUserInOffice && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.preventDefault();
                setChooseImageModalVisible(true);
                e.stopPropagation();
              }}
            >
              {!signature.isMoved && (
                <EditIcon className="bg-primary cursor-pointer edit-icon" />
              )}
            </div>
          )}
        <ChooseSignatureImageModal
          title={t(`signature.chooseImageModal.header.${transactionType}`, {
            firstname,
            lastname,
          })}
          defaultSelectedPictureRank={
            signaturePictureRank && signaturePictureRank > 0
              ? signaturePictureRank
              : undefined
          }
          defaultSignaturePriorityRule={signaturePriorityRule}
          userSignaturePictures={signatorySignaturePicturesWithCorrectUrl}
          officeSignaturePictures={officeSignaturePictures}
          visible={chooseImageModalVisible}
          onCancel={() => setChooseImageModalVisible(false)}
          onOk={() => setChooseImageModalVisible(false)}
          onSubmitNewPicture={handleSubmitChooseSignature}
          buttonText={t("signature.chooseImageModal.validate")}
        />
        {isFirstSignature && (
          <div className={"d-flex align-items-center position-relative"}>
            {signatureCount !== undefined && (
              <SignatureCounter
                count={signatureCount}
                isDisabled={isSignatoryCounterDisabled}
              />
            )}
          </div>
        )}
      </>
    </div>
  );
};

export default SignatoryCard;
